import { Component, OnInit, signal, WritableSignal } from '@angular/core';
import { NgFor, NgIf, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { FileUploadService } from '../../../services/file-upload/file-upload-service';
import { ToastService } from '../../../services/toast/toast.service';
import { HeaderComponent } from '../common/header/header.component';

@Component({
  selector: 'app-download',
  standalone: true,
  imports: [NgFor, NgIf, DatePipe, FormsModule, HeaderComponent],
  templateUrl: './download.component.html',
  styleUrl: './download.component.scss'
})
export class DownloadComponent implements OnInit {
  files: any[] = [];
  filteredFiles: any[] = [];
  loading: WritableSignal<boolean> = signal(false);
  searchTerm: string = '';
  selectedCategory: string = '';
  categories: string[] = [];

  constructor(
    private fileUploadService: FileUploadService,
    private toastService: ToastService
  ) {}

  ngOnInit() {
    this.loading.set(true);
    // Subscribe to the service's observable stream for real-time updates
    this.fileUploadService.getFilesStream().subscribe(files => {
      this.files = files;
      this.filteredFiles = files;
      this.extractCategories();
      this.loading.set(false);
    });

    // Initial load from API
    this.fileUploadService.getAllFiles().subscribe({
      error: (error) => {
        console.error('Error loading files:', error);
        this.toastService.makeToast('error', 'Failed to load files', 3000);
        this.loading.set(false);
      }
    });
  }

  loadFiles() {
    this.loading.set(true);
    this.fileUploadService.getAllFiles().subscribe({
      next: (files) => {
      },
      error: (error) => {
        console.error('Error loading files:', error);
        this.toastService.makeToast('error', 'Failed to load files', 3000);
        this.loading.set(false);
      }
    });
  }

  extractCategories() {
    const categorySet = new Set<string>();
    this.files.forEach(file => {
      if (file.category) {
        categorySet.add(file.category);
      }
    });
    this.categories = Array.from(categorySet).sort();
  }

  filterFiles() {
    this.filteredFiles = this.files.filter(file => {
      const matchesSearch = !this.searchTerm || 
        file.title?.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        file.description?.toLowerCase().includes(this.searchTerm.toLowerCase()) ||
        file.mainFileName?.toLowerCase().includes(this.searchTerm.toLowerCase());
      
      const matchesCategory = !this.selectedCategory || file.category === this.selectedCategory;
      
      return matchesSearch && matchesCategory;
    });
  }

  onSearchChange() {
    this.filterFiles();
  }

  onCategoryChange() {
    this.filterFiles();
  }

  clearFilters() {
    this.searchTerm = '';
    this.selectedCategory = '';
    this.filterFiles();
  }

  downloadFile(fileId: string, fileName: string) {
    if (!fileId) {
      this.toastService.makeToast('error', 'File ID not available', 3000);
      return;
    }

    this.fileUploadService.downloadFile(fileId).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName || 'download';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        this.toastService.makeToast('success', 'Download started!', 3000);
      },
      error: (error) => {
        console.error('Download error:', error);
        this.toastService.makeToast('error', 'Download failed. Please try again.', 3000);
      }
    });
  }

  viewLinkFile(fileUrl: string) {
    if (!fileUrl) {
      this.toastService.makeToast('error', 'Link not available', 3000);
      return;
    }

    try {
      window.open(fileUrl, '_blank');
    } catch (error) {
      console.error('View error:', error);
      this.toastService.makeToast('error', 'Failed to open link', 3000);
    }
  }

  copyToClipboard(text: string) {
    navigator.clipboard.writeText(text).then(() => {
      this.toastService.makeToast('success', 'Link copied to clipboard!', 2000);
    }).catch(() => {
      this.toastService.makeToast('error', 'Failed to copy link', 2000);
    });
  }

  getFileIcon(fileName: string): string {
    const extension = fileName?.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf': return 'pi pi-file-pdf';
      case 'doc':
      case 'docx': return 'pi pi-file-word';
      case 'zip':
      case 'rar': return 'pi pi-file-zip';
      case 'jpg':
      case 'jpeg': 
      case 'png':
      case 'gif': return 'pi pi-image';
      default: return 'pi pi-file';
    }
  }

  getCategoryColor(category: string): string {
    const colors: { [key: string]: string } = {
      'manual': '#3b82f6',
      'software': '#10b981',
      'documentation': '#f59e0b',
      'resources': '#8b5cf6',
      'other': '#6b7280',
      'rawfile': '#3b82f6',
      'linkfile': '#10b981'
    };
    return colors[category] || '#6b7280';
  }
}
